import React, {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {shallowEqual} from "react-redux";
import {RenderGrapesToHTML, traverseGrapesProject} from "./GrapesJSUtilities";
import {GjsActionTypes, GjsComponentTypes} from "./gjsplugin/Enums";
import FullCameraPreview from "./FullCameraPreview";
import FullScreenImagePreview from "./FullScreenImagePreview";
import CameraPairingsHtmlRenderer from "./CameraPairingsHtmlRenderer";
import types from '../uisystem/subcomponents/types';
import UIButtons from '../uisystem/subcomponents/UIButtons';
import UIContainer from '../uisystem/subcomponents/UIContainer';
import UIContinueButton from '../uisystem/subcomponents/UIContinueButton';
import UIHeaders from '../uisystem/subcomponents/UIHeaders';
import UIInsets from '../uisystem/subcomponents/UIInsets';
import UINextStep from '../uisystem/subcomponents/UINextStep';
import UIOptions from '../uisystem/subcomponents/UIOptions';
import UIPreviousStep from '../uisystem/subcomponents/UIPreviousStep';
import UIProjectOptions from '../uisystem/subcomponents/UIProjectOptions';
import UIRetryButton from '../uisystem/subcomponents/UIRetryButton';
import UIScoreNumber from '../uisystem/subcomponents/UIScoreNumber';
import UITextDescription from '../uisystem/subcomponents/UITextDescription';
import {useTypedDispatch, useTypedSelector} from "../../state/hooks";
import {GrapesRenderModes, IDisplayDeviceInfo, IGrapesJSProjectData} from "../../interfaces/VisageUI";
import {
	ChecklistFormType,
	ChecklistPriority,
	CustomEventTypes,
	SimulationStates,
	UISidebarContentTypes
} from "../../globals";
import store, {StoreActions} from "../../state/store";
import UISidebarAttachments from "../attachments/UISidebarAttachments";
import ChecklistPreviewContainer from "../checklist/ChecklistPreviewContainer";
import {toggleSidebarContent} from '../uisystem/UIUtilities';
import Button from "../layout/Button";
import Scenario from '../../classes/scenarioElements/Scenario';
import {
	AttachmentsIcon,
	ChevronRight,
	ClearIcon,
	CollapseAll,
	EyeIcon,
	EyeIconOff,
	FormIcon,
	IconFillStyles,
	MenuIcon,
	PartsCatalogIcon,
	RefreshIcon
} from "../Iconset";
import QuizManager from '../../classes/QuizManager';
import UIVideoPlayer from "../uisystem/UIVideoPlayer";
import Engine from '../../classes/Engine';
import DomUtils from "../../classes/utilities/DomUtils";
import PartsCatalogPreviewContainer from "../partsCatalog/PartsCatalogPreviewContainer";
import PartsCatalogUtils from "../../classes/utilities/PartsCatalogUtils";
import {FeatureFlagNames} from "../../interfaces/enums";
import {AppState} from "../../classes/AppState";
import TextVariables from "../../classes/TextVariables";
import {JsonCopy} from "../../classes/utilities/Misc";
import TrackersPreviewContainer from "../trackersUI/TrackersPreviewContainer";
import useCurrentStep from "../reactUtils/useCurrentStep";
import useForceUpdate from "../reactUtils/useForceUpdate";
import useEventListener from "../reactUtils/useEventListener";
import SessionUtils from "../../classes/utilities/SessionUtils";

export function renderOldUITemplate(templateData: any, previewScale: number, simulationState: SimulationStates, templateName: string, withWarnings = true): JSX.Element[]
{
	const output: JSX.Element[] = [];
	if (templateData)
	{
		for (const key of Object.keys(templateData))
		{
			const commonProps = {
				context: simulationState !== SimulationStates.Playing ? 'preview' : 'simulation',
				data: templateData[key],
				scale: previewScale * 100 / 60,
				template: templateName,
			}
			switch (key)
			{
				case types.NEXT_STEP:
					output.push(<UINextStep key={key} {...commonProps} />)
					break;
				case types.PREVIOUS_STEP:
					output.push(<UIPreviousStep key={key} {...commonProps} />)
					break;
				case types.PROJECT_OPTIONS:
					output.push(<UIProjectOptions key={key} {...commonProps} />)
					break;
				case types.HEADERS:
					output.push(<UIHeaders {...commonProps} />)
					break;
				case types.OPTIONS:
					output.push(<UIOptions key={key} {...commonProps} />)
					break;
				case types.TEXT_DESCRIPTION:
					output.push(<UITextDescription key={key} {...commonProps} />)
					break;
				case types.CONTAINER:
					output.push(<UIContainer key={key} {...commonProps} />)
					break;
				case types.BUTTONS:
					output.push(<UIButtons key={key} {...commonProps} />)
					break;
				case types.INSETS:
					output.push(<UIInsets key={key} {...commonProps} />)
					break;
				case types.CONTINUE_BUTTON:
					output.push(<UIContinueButton key={key} {...commonProps} />)
					break;
				case types.RETRY_BUTTON:
					output.push(<UIRetryButton key={key} {...commonProps} />)
					break;
				case types.SCORE_NUMBER:
					output.push(<UIScoreNumber key={key} {...commonProps} showWarnings={withWarnings}/>)
					break;
				default:
					break;
			}
		}
	}

	return output;
}

export function GenerateOldUiContainerStyle(wrapperRect: { width: number, height: number }, currentDisplayDevice: IDisplayDeviceInfo, simulationAsViewer: boolean)
{
	if (!wrapperRect || !currentDisplayDevice?.resolution || simulationAsViewer) return {};

	let width: number, height: number;

	// Compute the aspect ratio of the current display device
	const deviceAspectRatio = currentDisplayDevice.resolution.width / currentDisplayDevice.resolution.height;

	// Determine whether to scale based on width or height
	const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

	if (wrapperAspectRatio > deviceAspectRatio) 
	{
		// The wrapper is wider than the device aspect ratio, so we clamp to height
		height = Math.min(wrapperRect.height - 4, currentDisplayDevice.resolution.height);
		width = height * deviceAspectRatio;
	}
	else 
	{
		// The wrapper is taller, so we clamp to width
		width = Math.min(wrapperRect.width - 4, currentDisplayDevice.resolution.width);
		height = width / deviceAspectRatio;
	}

	// Centering the content within the wrapper
	const left = (wrapperRect.width - width) / 2;
	const top = (wrapperRect.height - height) / 2;

	return {
		left: left + "px",
		top: top + "px",
		width: width + "px",
		height: height + "px",
	};
}

export default function VisagePreview(props: {
	wrapperRect: { width: number, height: number };
	inputReceiverRef: { current: any; };
	overlayMenu?: ReactNode;
})
{
	const currentStep = useCurrentStep();
	const dispatch = useTypedDispatch();
	const forceUpdate = useForceUpdate();

	const stepUIData: IGrapesJSProjectData = useTypedSelector(state => state.visageModule.stepUIData[currentStep.id]);
	const simulationState: SimulationStates = useTypedSelector(state => state.applicationModule.simulationState);
	const simulationAsViewer = useTypedSelector(state => state.applicationModule.simulationAsViewer);
	const showUiPreview: boolean = useTypedSelector(state => state.uiModule.previewVisible);
	const previewScale: number = useTypedSelector(state => state.visageModule.uiPreviewScale);
	const currentDisplayDevice: IDisplayDeviceInfo = useTypedSelector(state => state.visageModule.currentDisplayDevice);
	const sidebarContent: UISidebarContentTypes = useTypedSelector(state => state.uiModule.sidebarContent, shallowEqual);
	const checklistSimulation = useTypedSelector(state => state.checklistModule.simulationEvent, shallowEqual);
	const stepChecklist = useTypedSelector(state => state.checklistModule.checklist[currentStep.id], shallowEqual);
	const stepChecklistItems = useTypedSelector(state => !stepChecklist || stepChecklist.type === ChecklistFormType.NONE ? null : stepChecklist.type === ChecklistFormType.QUIZ ? stepChecklist.questions : stepChecklist.items, shallowEqual);
	const hasChecklist = stepChecklistItems && stepChecklistItems.length && stepChecklistItems.length > 0 ? true : false;
	const stepAttachments = useTypedSelector(state => state.attachmentsModule.attachments[currentStep.id], shallowEqual);
	const hasAttachments = stepAttachments?.length > 0;
	const partsCatalog = useTypedSelector(state => state.partsCatalogModule.catalogs[currentStep.id], shallowEqual);
	const hasPartsCatalog = useTypedSelector(state => state.partsCatalogModule.catalogs[currentStep.id]?.enabled, shallowEqual);
	const selectedPart = useTypedSelector(state => state.partsCatalogModule.selectedPart[currentStep.id]);
	const simulationIsolatedObjects = useTypedSelector(state => state.partsCatalogModule.simulationIsolatedObjects);
	const simulationHighlightedObjects = useTypedSelector(state => state.partsCatalogModule.simulationHighlightedObjects);
	const showGhostedUnselected = useTypedSelector(state => state.partsCatalogModule.showGhostedUnselected);
	const showCollapseAll = useTypedSelector(state => state.partsCatalogModule.showCollapseAll);
	const showOverlayMenu = useTypedSelector(state => state.visageModule.showOverlayMenu);

	//#region Attempt to render old UI here and retire old UIPreview and UISimulation garbage
	const editingSlide = useTypedSelector(state => state.uiModule.editingSlide, shallowEqual);
	const stepData = useTypedSelector(state => editingSlide ? state.uiModule.slideMaster[editingSlide] : state.uiModule.ui[currentStep.id], shallowEqual);

	let oldUiRenderer: JSX.Element[] = [];
	if (stepData)
	{
		const active = stepData.active;
		const templateData = stepData[active];
		if (templateData)
		{
			oldUiRenderer = renderOldUITemplate(templateData, previewScale, simulationState, active.toLowerCase().replace(' ', '-'));
		}
	}

	//#endregion

	// video player TODO: this is old system stuff. will probably need to update
	const videoUrl = useTypedSelector(state => state.uiModule.videoPlayerUrl, shallowEqual);
	const audioOnly = useTypedSelector(state => state.uiModule.audioOnly, shallowEqual);
	const fullscreenAssetUrl = useTypedSelector(state => state.uiModule.fullscreenAssetUrl, shallowEqual);

	const [enlargedCamera, setEnlargedCamera] = useState<string>("");
	const [previewContainerStyle, setPreviewContainerStyle] = React.useState<React.CSSProperties>({});
	const [htmlDoc, setHtmlDoc] = useState<Document | null>(null);
	const [ioTEventCounter, setIoTEventCounter] = useState(0);

	const rootRef = useRef<HTMLDivElement>(null);
	const frameRef = useRef<HTMLIFrameElement>(null);
	const frameScale = useRef<number>(1);
	const previewContainerElement = useRef<HTMLDivElement>();

	useEventListener(CustomEventTypes.onIOTValueUpdated, () => { setIoTEventCounter(ioTEventCounter + 1) }, document);
	useEventListener("message", handleIframeMessage, window);
	useEventListener('mousemove', (e) =>
	{
		if (rootRef.current)
		{
			Engine.getInstance().getTriggerManager().blockTriggers(DomUtils.isDomElementDescendantOfRoot(e.target, rootRef.current));
		}
	}, document);

	useEffect(() =>
	{
		if (simulationState === SimulationStates.Playing)
		{
			if (
				stepChecklist &&
				stepChecklist.behaviour &&
				stepChecklist.behaviour === ChecklistPriority.AUTOOPEN &&
				stepChecklist.type !== ChecklistFormType.NONE &&
				(stepChecklistItems?.length ?? 0) > 0
			)
			{
				dispatch(StoreActions.ui.setSidebarContent(UISidebarContentTypes.Form));
				dispatch(StoreActions.checklist.setPreviewProperties({ simulationEvent: ChecklistPriority.AUTOOPEN, next: currentStep.getNextStep()?.id }));
			}
			else if (SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.partsCatalog) && partsCatalog?.enabled && partsCatalog?.autoOpen)
			{
				dispatch(StoreActions.ui.setSidebarContent(UISidebarContentTypes.PartsCatalog));
				dispatch(StoreActions.checklist.setPreviewProperties({ simulationEvent: ChecklistPriority.AUTOOPEN }));
			}
			else
			{
				dispatch(StoreActions.ui.setSidebarContent(UISidebarContentTypes.None));
				dispatch(StoreActions.checklist.setPreviewProperties({ simulationEvent: "" }));
			}
		}
		else
		{
			dispatch(StoreActions.scenario.clearStepToHistory());
			dispatch(StoreActions.ui.setSidebarContent(""));
			dispatch(StoreActions.checklist.setPreviewProperties({ simulationEvent: "" }));
			if (videoUrl)
			{
				dispatch(StoreActions.ui.showVideoPlayer({
					visible: false,
					url: "",
					audioOnly: false,
				}));
			}
			Engine.getInstance().getTriggerManager().blockTriggers(false);
		}

	}, [currentStep, simulationState]);

	useEffect(() =>
	{
		if (!frameRef.current) return;

		const doc = frameRef.current.contentDocument;
		if (!doc) return;

		doc.open();
		RenderGrapesToHTML(processTextVariables(stepUIData), GrapesRenderModes.Preview)
			.then((html) =>
			{
				doc.write(html);
				if(!doc.body) throw new Error("No body element found in the iframe document");

				// setup input forwarding from the iframe to the parent window
				function addEventListeners(element)
				{
					element.addEventListener("mousedown", passMouseEvent);
					element.addEventListener("mouseup", passMouseEvent);
					element.addEventListener("mousemove", passMouseEvent);
					element.addEventListener('wheel', passMouseEvent);
					element.addEventListener('contextmenu', ignoreEvent);
					element.addEventListener('keydown', passKeyboardEvent);
					element.addEventListener('keyup', passKeyboardEvent);
					element.addEventListener('touchstart', passTouchEvent, { passive: false });
					element.addEventListener('touchend', passTouchEvent, { passive: false });
					element.addEventListener('touchmove', passTouchEvent, { passive: false });
				}

				const allIframeElements = doc.body.querySelectorAll("*:not(input):not(script)");
				for (const iframeElement of allIframeElements)
				{
					if (iframeElement.attributes.getNamedItem("onclick")) continue;

					addEventListeners(iframeElement as HTMLElement);
				}
				addEventListeners(doc);
				setHtmlDoc(doc);
			})
			.catch((error) =>
			{
				console.error("Error rendering grapes to HTML", error);
			})
			.finally(() => 
			{
				doc.close();
			});
	}, [stepUIData, frameRef.current, ioTEventCounter]);

	const containerRef = useCallback((node: HTMLDivElement) => 
	{
		if (!node) return;

		const rootRect = props.wrapperRect;
		const containerRect = node.getBoundingClientRect();
		if (!rootRect || !containerRect) 
		{
			setPreviewContainerStyle({});
			return;
		}
		
		const newContainerStyle: React.CSSProperties =
		{
			width: currentDisplayDevice.resolution.width + "px",
			height: currentDisplayDevice.resolution.height + "px",
			transformOrigin: "top left",
			transform: "",
			overflow: "hidden",
		};

		let actualWidth = currentDisplayDevice.resolution.width;
		let actualHeight = currentDisplayDevice.resolution.height;

		// scale content down if necessary (up to original inteded size)
		if (currentDisplayDevice.resolution.width > rootRect.width || currentDisplayDevice.resolution.height > rootRect.height)
		{
			const scaleX = rootRect.width / currentDisplayDevice.resolution.width;
			const scaleY = rootRect.height / currentDisplayDevice.resolution.height;
			frameScale.current = Math.min(scaleX, scaleY);
			newContainerStyle.transform = `scale(${frameScale.current})`;

			actualWidth *= frameScale.current;
			actualHeight *= frameScale.current;

			if (frameScale.current !== 0)
			{
				dispatch(StoreActions.visage.setUIPreviewScale(frameScale.current));
			}
		}

		// modify the sidebar container to fit the calculated size
		const newSidebarContainerStyle: React.CSSProperties = {
			position: "absolute",
			width: (actualWidth - 2) + "px",
			height: (actualHeight - 4) + "px",
		};

		// center content in container if it's smaller than the container
		if (actualWidth < rootRect.width)
		{
			const offsetX = (rootRect.width - actualWidth) / 2;
			newContainerStyle.left = offsetX + "px";
			newSidebarContainerStyle.left = (offsetX - 2) + "px";
		}
		if (actualHeight < rootRect.height)
		{
			const offsetY = (rootRect.height - actualHeight) / 2;
			newContainerStyle.top = offsetY + "px";
			newSidebarContainerStyle.top = (offsetY + 2) + "px";
		}

		setPreviewContainerStyle({ ...newContainerStyle });
		previewContainerElement.current = node;
	}, [currentDisplayDevice, props.wrapperRect, simulationState, simulationAsViewer]);

	useEffect(() => 
	{
		forceUpdate();
	}, [checklistSimulation, sidebarContent]);

	if (!showUiPreview) return null;

	function handleIframeMessage(event: MessageEvent)
	{
		if (event.data.type === "iframeEvent" && props.inputReceiverRef.current && previewContainerElement.current)
		{
			//console.log("iframeEvent", event.data.detail.type, event.data.detail);

			// mouse events
			if (["mousedown", "mouseup", "mousemove", "click", "dblclick", "wheel", "touchstart", "touchend", "touchmove"].includes(event.data.detail.type))
			{
				// calculate offsets
				const containerRect = previewContainerElement.current.getBoundingClientRect();

				// create a new event based on the data received
				const { type, x, y } = event.data.detail;
				const eventData = {
					clientX: (x * frameScale.current) + containerRect.left,
					clientY: (y * frameScale.current) + containerRect.top,
					bubbles: true,
					cancelable: true,
				};



				let newEvent: MouseEvent | WheelEvent;
				if (type === "wheel")
				{
					newEvent = new WheelEvent(type, {
						...eventData,
						deltaX: event.data.detail.deltaX,
						deltaY: event.data.detail.deltaY,
						deltaZ: event.data.detail.deltaZ,
						deltaMode: event.data.detail.deltaMode,
					});
				}
				else
				{
					newEvent = new MouseEvent(type, {
						...eventData,
						button: event.data.detail.button,
					});
				}

				if (type === "touchstart" || type === "touchend" || type === "touchmove")
				{
					newEvent["touches"] = event.data.detail.touches.map(touch => ({
						clientX: (touch.clientX * frameScale.current) + containerRect.left,
						clientY: (touch.clientY * frameScale.current) + containerRect.top,
					}));
				}

				// send the event as if it came from the scene canvas
				props.inputReceiverRef.current.dispatchEvent(newEvent);
			}
			// keyboard events
			else if (["keydown", "keyup"].includes(event.data.detail.type))
			{
				const { type, key, code, keyCode, altKey, ctrlKey, shiftKey, metaKey } = event.data.detail;
				const newEvent = new KeyboardEvent(type, { key, code, keyCode, altKey, ctrlKey, shiftKey, metaKey, bubbles: true, cancelable: true });
				props.inputReceiverRef.current.dispatchEvent(newEvent);
			}
		}
		else if (event.data.type === "messageToUnity")
		{
			//console.log("messageToUnity", event.data.detail);
			switch (event.data.detail.type)
			{
				case GjsActionTypes.MoveToStep:
					const targetStep = Scenario.getStep(event.data.detail.stepId);
					if (targetStep)
					{
						Scenario.setCurrentStep(targetStep);
					}
					break;
				case GjsActionTypes.NextStep:
					let nextStep;

					if (store.getState().uiModule.nextStepOverride)
					{
						nextStep = Scenario.getStep(store.getState().uiModule.nextStepOverride);
						store.dispatch(StoreActions.ui.setNextStepOverride(""));
					}
					else
					{
						nextStep = Scenario.currentStep()?.getNextStep();
					}

					if (nextStep)
					{
						Scenario.setCurrentStep(nextStep);
					}
					break;
				case GjsActionTypes.PreviousStep:
					Scenario.goBackInStepHistory(1);
					break;
				case GjsActionTypes.OpenURL:
					let rawUrl = event.data.detail.url;
					if (!rawUrl.match(/^[a-zA-Z]+:\/\//)) 
					{
						rawUrl = "http://" + rawUrl;
					}
					window.open(rawUrl, "_blank");
					break;
				case GjsActionTypes.OpenAttachment:
					window.open(event.data.detail.path, "_blank");
					break;
				case GjsActionTypes.FinishProcedure:
					AppState.stopSimulation();
					break;
				case GjsActionTypes.RetryQuiz:
					const currentNumberOfRetries = QuizManager.GetCurrentNumberOfRetries();
					QuizManager.SetCurrentNumberOfRetries(currentNumberOfRetries - 1);
					AppState.restartSimulation(true); // https://scopeardev.slack.com/archives/C02V4RGG2CA/p1664811827039599?thread_ts=1664802292.569479&cid=C02V4RGG2CA
					break;
				case GjsActionTypes.PlayVideo:
					dispatch(StoreActions.ui.showVideoPlayer({ visible: true, url: event.data.detail.path }));
					break;
				case GjsActionTypes.PlayAudio:
					dispatch(StoreActions.ui.showVideoPlayer({ visible: true, url: event.data.detail.path, audioOnly: true }));
					break;
				case GjsActionTypes.EnlargeCamera:
					setEnlargedCamera(event.data.detail.cameraId);
					break;
				case GjsActionTypes.FullScreen:
					console.log("FullScreen", event.data.detail.path);
					dispatch(StoreActions.ui.setFullscreenAssetUrl(event.data.detail.path));
					break;
				case GjsActionTypes.CompleteQuiz:
				case GjsActionTypes.ScenarioLink:
				case GjsActionTypes.None:
				default:
					console.log("Unknown messageToUnity type", event.data.detail.type);
					break;
			}
		}
	}

	function processTextVariables(stepUIData: IGrapesJSProjectData): IGrapesJSProjectData
	{

		const processedData = JsonCopy(stepUIData);
		traverseGrapesProject(processedData, ({ component }) => 
		{
			if (component.type === GjsComponentTypes.Text || component.type === GjsComponentTypes.TextNode)
			{
				component.content = TextVariables.textToData(component.content!);
			}
		});

		return processedData;
	}

	function passMouseEvent(e: MouseEvent | WheelEvent)
	{
		if (e.target?.["attributes"]?.getNamedItem("onclick")) return;

		const eventDetail = {
			type: e.type,
			x: e.clientX,
			y: e.clientY,
		};

		switch (e.type)
		{
			case "wheel":
				eventDetail["deltaX"] = e["deltaX"];
				eventDetail["deltaY"] = e["deltaY"];
				eventDetail["deltaZ"] = e["deltaZ"];
				eventDetail["deltaMode"] = e["deltaMode"];
				break;
			default:
				eventDetail["button"] = e["button"];
		}

		window.parent.postMessage({ type: "iframeEvent", detail: { ...eventDetail } }, "*");
	}

	function passTouchEvent(e: TouchEvent)
	{
		if (e.target?.["attributes"]?.getNamedItem("onclick")) return;

		if (e.type === "touchstart" || e.type === "touchmove")
		{
			if (e.touches.length > 1)
			{
				e.preventDefault();
			}
		}

		const eventDetail = {
			type: e.type,
			x: e.touches[0]?.clientX ?? 0,
			y: e.touches[0]?.clientY ?? 0,
			touches: Array.from(e.touches).map(touch => ({
				clientX: touch.clientX,
				clientY: touch.clientY,
			})),
		};

		window.parent.postMessage({ type: "iframeEvent", detail: { ...eventDetail } }, "*");
	}

	function passKeyboardEvent(e: KeyboardEvent)
	{
		if (e.target !== frameRef.current?.contentDocument?.body) return;

		e.preventDefault();
		e.stopPropagation();

		const eventDetail = {
			type: e.type,
			key: e.key,
			code: e.code,
			keyCode: e.keyCode,
			altKey: e.altKey,
			ctrlKey: e.ctrlKey,
			shiftKey: e.shiftKey,
			metaKey: e.metaKey,
		};

		window.parent.postMessage({ type: "iframeEvent", detail: { ...eventDetail } }, "*");
	}

	function ignoreEvent(e)
	{
		e.preventDefault();
		e.stopPropagation();
	}

	function renderSidebar()
	{
		switch (sidebarContent)
		{
			case UISidebarContentTypes.Attachments:
				return <UISidebarAttachments stepId={currentStep.id} />;
			case UISidebarContentTypes.Form:
				return <ChecklistPreviewContainer step={currentStep.id} />;
			case UISidebarContentTypes.PartsCatalog:
				return <PartsCatalogPreviewContainer step={currentStep.id} />;
			default:
				return null;
		}
	}

	const hideSidebar = fullscreenAssetUrl || (!(sidebarContent && (hasAttachments || hasChecklist || hasPartsCatalog)) || !checklistSimulation);
	const currentSidebar = !hideSidebar ? sidebarContent : '';

	return (
		<div className={"visagePreviewRoot"}
			data-name={"ui-preview-root"}
			ref={rootRef}
		>
			{/* ----------------- */}
			{/* Legacy UI Preview */}
			{/* ----------------- */}
			<div id={"trackersUserDefinedPoseContainer"}
				className={[
					simulationState !== SimulationStates.Playing ? "uiPreviewRoot" : "uiSimulationRoot",
					simulationAsViewer ? "viewer" : "",
				].join(" ")}
				data-name={"ui-preview-root"}				
				data-status={editingSlide ? "EditingTemplate" : currentStep.isTrackersStep() ? "EditingTrackers" : "Normal"}
				style={GenerateOldUiContainerStyle(props.wrapperRect, currentDisplayDevice, simulationAsViewer)}
			>
				{
					currentStep.isTrackersStep() && !editingSlide ? <TrackersPreviewContainer /> : null
				}
				{
					(!sidebarContent || !checklistSimulation) ? oldUiRenderer : null
				}
				{
					!hideSidebar && renderSidebar()
				}
				{
					!hideSidebar && 
						<Button className={"sidebarCloseButtonVisage"}
							data-name={"sidebar-close-button"}
							onClick={() => dispatch(StoreActions.checklist.setPreviewProperties({ simulationEvent: "" }))}
							iconRenderer={<ClearIcon scale={1.2} />}
						/>
				}
				{
					checklistSimulation === "" || selectedPart || (checklistSimulation && sidebarContent !== UISidebarContentTypes.PartsCatalog) || !showCollapseAll ? null :
						<Button className={"sidebarCollapseButton"}
							data-name={"sidebar-collapse-button"}
							onClick={() =>
							{
								if (simulationState !== SimulationStates.Playing) return;
								dispatch(StoreActions.partsCatalog.setShowCollapseAll(false));
								dispatch(StoreActions.partsCatalog.clearSimulationExpandedObjects());
							}} // collapse all
							iconRenderer={<CollapseAll scale={1.4} />}
						/>
				}
				{
					checklistSimulation === "" || !selectedPart || (checklistSimulation && sidebarContent !== UISidebarContentTypes.PartsCatalog) || !selectedPart.nextPart ? null :
						<Button className={"sidebarNavRightButton"}
							data-name={"sidebar-nav-right-button"}
							onClick={() =>
							{
								if (simulationState !== SimulationStates.Playing) return;
								if (selectedPart && selectedPart.nextPart)
								{
									const part = PartsCatalogUtils.GetFlattenedPart(currentStep.id, selectedPart.nextPart);
									dispatch(StoreActions.partsCatalog.setSelectedPart({ stepId: currentStep.id, part: part }));
								}
							}} // navigate to next item
							iconRenderer={<ChevronRight scale={1.5} />}
						/>
				}
				{
					checklistSimulation === "" || !selectedPart || (checklistSimulation && sidebarContent !== UISidebarContentTypes.PartsCatalog) || !selectedPart.previousPart ? null :
						<Button className={"sidebarNavLeftButton"}
							data-name={"sidebar-nav-left-button"}
							onClick={() =>
							{
								if (simulationState !== SimulationStates.Playing) return;
								if (selectedPart && selectedPart.previousPart)
								{
									const part = PartsCatalogUtils.GetFlattenedPart(currentStep.id, selectedPart.previousPart);
									dispatch(StoreActions.partsCatalog.setSelectedPart({ stepId: currentStep.id, part: part }));
								}
							}} // navigate to previous item
							iconRenderer={<ChevronRight rotate={180} scale={1.5} />}
						/>
				}
				{
					checklistSimulation === "" || !selectedPart || (checklistSimulation && sidebarContent !== UISidebarContentTypes.PartsCatalog) ? null :
						<Button className={"sidebarRefreshButton"}
							data-name={"sidebar-eye-button"}
							onClick={() =>
							{
								if (simulationState !== SimulationStates.Playing) return;
								dispatch(StoreActions.partsCatalog.toggleShowGhostedUnselected());
								PartsCatalogUtils.ShowGhostedUnselected(!showGhostedUnselected);
								PartsCatalogUtils.PartFocus(selectedPart.id, false);
							}}
							iconRenderer={showGhostedUnselected ? <EyeIcon scale={1.35} /> : <EyeIconOff scale={1.35} />}
						/>
				}
				{
					checklistSimulation === "" || selectedPart || (checklistSimulation && sidebarContent !== UISidebarContentTypes.PartsCatalog) || (simulationIsolatedObjects[currentStep.id]?.length === 0 && simulationHighlightedObjects[currentStep.id]?.length === 0) ? null :
						<Button className={"sidebarRefreshButton"}
							data-name={"sidebar-refresh-button"}
							onClick={() =>
							{
								if (simulationState !== SimulationStates.Playing) return;
								dispatch(StoreActions.partsCatalog.clearSimulationObjects());
								PartsCatalogUtils.buildCatalogObjectsVisibility();
							}}
							iconRenderer={<RefreshIcon />}
						/>
				}
				{
					currentStep.isTrackersStep() && !editingSlide ? null :
						<div className={"uiMenuContainer"}>
							<Button className={"uiMenuButton"}
								data-name={"sidebar-menu-button"}
								onClick={() => 
								{
									dispatch(StoreActions.checklist.setPreviewProperties({ simulationEvent: "" }));
									dispatch(StoreActions.visage.setShowOverlayMenu(!showOverlayMenu));
								}}
								iconRenderer={<MenuIcon scale={2.2} />}
							/>
							{
								!hasAttachments ? null :
									<Button className={"uiMenuButton"}
										data-name={"attachments-show-button"}
										active={currentSidebar === UISidebarContentTypes.Attachments ? true : false}
										category={"Attachments"}
										onClick={() => { toggleSidebarContent(UISidebarContentTypes.Attachments); }}
										iconRenderer={<AttachmentsIcon scale={2.2} fillStyle={currentSidebar === UISidebarContentTypes.Attachments ? IconFillStyles.dark : undefined} />}
									/>
							}
							{
								!SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.partsCatalog) || !hasPartsCatalog ? null :
									<Button className={"uiMenuButton"}
										data-name={"sidebar-menu-button"}
										active={currentSidebar === UISidebarContentTypes.PartsCatalog ? true : false}
										onClick={() => { toggleSidebarContent(UISidebarContentTypes.PartsCatalog); }}
										iconRenderer={<PartsCatalogIcon scale={1.2} fillStyle={currentSidebar === UISidebarContentTypes.PartsCatalog ? IconFillStyles.dark : undefined} />}
									/>
							}
							{
								!hasChecklist ? null :
									<Button className={"uiMenuButton"}
										data-name={"checklist-show-button"}
										active={currentSidebar === UISidebarContentTypes.Form ? true : false}
										category={"Checklist"}
										onClick={() => { toggleSidebarContent(UISidebarContentTypes.Form); }}
										iconRenderer={<FormIcon scale={2.2} fillStyle={currentSidebar === UISidebarContentTypes.Form ? IconFillStyles.dark : undefined} />}
									/>
							}
							{
								showOverlayMenu ? props.overlayMenu : null
							}
						</div>
				}
			</div>

			{/* ----------------- */}
			{/* Visage UI Preview */}
			{/* ----------------- */}
			{
				editingSlide || currentStep.isTrackersStep() ? null :
					<div data-name={"ui-preview-container"}
						ref={containerRef}
						className={["visagePreviewContainer", simulationAsViewer ? "viewer" : ""].join(" ")}
						style={previewContainerStyle}
						data-simulation-state={simulationState}
					>
						{
							currentStep.isTrackersStep() && !editingSlide ? null :
								<CameraPairingsHtmlRenderer htmlDocument={htmlDoc} />
						}
						<UIVideoPlayer videoUrl={videoUrl} audioOnly={audioOnly} />
						{
							enlargedCamera && <FullCameraPreview cameraId={enlargedCamera} onClick={() => setEnlargedCamera("")} />
						}
						{
							fullscreenAssetUrl && <FullScreenImagePreview assetSrc={fullscreenAssetUrl} onClose={() => dispatch(StoreActions.ui.setFullscreenAssetUrl(""))} />
						}
						{
							!hideSidebar || !stepUIData || !Object.keys(stepUIData).length ? null :
								<iframe data-name={"ui-preview-frame"}
									className={"visagePreviewFrame"}
									ref={frameRef}
								/>
						}
					</div>
			}
		</div>
	);
}
